import { useEffect, useRef, useState } from 'react';
import { Col, Input, Progress, Row, Spinner } from 'reactstrap';
import { Database, Play, XCircle, Search, ChevronDown, ChevronUp } from 'react-feather';
import Table from '../ui/Table';
import calculadoraNotasApiService from '../../services/http/calculadoraNotasApiService';
import colors from '../../theme/colors';

const FormularioMotorCalculo = () => {
  const canceladoRef = useRef(false);
  const [showFilters, setShowFilters] = useState(true);
  const [isDataLoading, setIsDataLoading] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [dataApi, setDataApi] = useState([]);
  const [anoAcademico, setAnoAcademico] = useState('');
  const [totalRecords, setTotalRecords] = useState(0);
  const [recordsProcessed, setRecordsProcessed] = useState(0);
  const [progressPercentage, setProgressPercentage] = useState(0);
  const [startTime, setStartTime] = useState(Date.now());
  const [estimatedTimeRemaining, setEstimatedTimeRemaining] = useState(null);

  const estadoOk = <b style={{ color: '#0bb7af' }}>OK</b>;
  const estadoKo = <b style={{ color: '#f64e60' }}>KO</b>;
  const estadoProc = (
    <>
      <b style={{ color: '#ee9d01' }}>Procesando</b>{' '}
      <Spinner color="warning" style={{ height: '15px', width: '15px' }} />
    </>
  );

  useEffect(() => {
    const fetchCursoActual = async () => {
      try {
        const data = await calculadoraNotasApiService.mostrarCursoActual({ headquarter: 'CEDESCA' });
        setAnoAcademico(data);
      } catch (error) {
        setAnoAcademico('');
      }
    };
    fetchCursoActual();
  }, []);

  useEffect(() => {
    const currentTime = Date.now();
    const elapsedTime = currentTime - startTime;
    const speed = recordsProcessed / elapsedTime;
    const remainingRecords = totalRecords - recordsProcessed;
    const remainingTime = remainingRecords / speed;
    const minutes = Math.floor(remainingTime / (1000 * 60));
    const seconds = Math.floor((remainingTime % (1000 * 60)) / 1000);
    setEstimatedTimeRemaining(`${minutes} minutos y ${seconds} segundos`);
  }, [recordsProcessed, totalRecords, startTime]);

  useEffect(() => {
    setProgressPercentage((recordsProcessed * 100) / totalRecords);
  }, [recordsProcessed, totalRecords]);

  const handleLoadData = async () => {
    setIsDataLoading(true);
    try {
      const data = await calculadoraNotasApiService.mostrarAlumnos({ headquarter: 'CEDESCA' });
      setDataApi(data);
      setDataLoaded(true);
      setTotalRecords(data.length);
    } catch (error) {
      setDataApi([]);
      setDataLoaded(false);
      setTotalRecords(0);
    } finally {
      setIsDataLoading(false);
    }
  };

  const handleCancel = () => { canceladoRef.current = true; };

  const procesarRegistro = async (i) => {
    if (canceladoRef.current) return;
    if (i >= dataApi.length) return;

    const updatedDataApi = [...dataApi];
    updatedDataApi[i].estado = estadoProc;
    setDataApi(updatedDataApi);

    const { id, idplan, idplan2, centro } = updatedDataApi[i];
    try {
      const data = await calculadoraNotasApiService.calcularNotas({
        id,
        idplan,
        idplan2,
        centro,
      });
      updatedDataApi[i].estado = estadoOk;
      updatedDataApi[i].notaCiclo = data.notaCiclo;
      setDataApi(updatedDataApi);
      setRecordsProcessed((prev) => prev + 1);
    } catch (error) {
      updatedDataApi[i].estado = estadoKo;
      setDataApi(updatedDataApi);
      setRecordsProcessed((prev) => prev + 1);
    }

    if (i < dataApi.length) {
      await procesarRegistro(i + 1);
    }
  };

  const handleRun = async () => {
    canceladoRef.current = false;
    setIsRunning(true);
    setStartTime(Date.now());
    await procesarRegistro(0);
    setIsRunning(false);
  };

  const toolbarButtons = (
    <>
      <button
        type="button"
        onClick={handleLoadData}
        disabled={isDataLoading}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '6px 14px',
          fontSize: '0.75rem', fontWeight: 600, borderRadius: '6px', border: 'none',
          backgroundColor: colors.primary, color: '#fff',
          cursor: isDataLoading ? 'not-allowed' : 'pointer',
          opacity: isDataLoading ? 0.5 : 1, transition: 'all 0.15s',
        }}
      >
        {isDataLoading ? <Spinner style={{ height: '13px', width: '13px' }} /> : <Database size={13} />}
        {isDataLoading ? 'Cargando...' : 'Cargar datos'}
      </button>
      {isRunning ? (
        <button
          type="button"
          onClick={handleCancel}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '6px 14px',
            fontSize: '0.75rem', fontWeight: 600, borderRadius: '6px', border: 'none',
            backgroundColor: '#dc2626', color: '#fff', cursor: 'pointer', transition: 'all 0.15s',
          }}
        >
          <XCircle size={13} />
          Cancelar
        </button>
      ) : (
        <button
          type="button"
          onClick={handleRun}
          disabled={dataApi.length === 0 || isRunning}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '6px 14px',
            fontSize: '0.75rem', fontWeight: 600, borderRadius: '6px',
            border: `1px solid ${colors.primary}`, backgroundColor: '#fff', color: colors.primary,
            cursor: (dataApi.length === 0 || isRunning) ? 'not-allowed' : 'pointer',
            opacity: (dataApi.length === 0 || isRunning) ? 0.5 : 1, transition: 'all 0.15s',
          }}
        >
          <Play size={13} />
          Ejecutar cálculo
        </button>
      )}
    </>
  );

  return (
    <>
      <div style={{
        backgroundColor: '#fff', borderRadius: '12px',
        border: '1px solid #e5e7eb', boxShadow: '0 1px 3px rgb(0 0 0 / 0.06)',
        overflow: 'visible',
      }}>
        {/* Toolbar */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '12px 16px',
          borderBottom: showFilters ? '1px solid #f3f4f6' : 'none',
        }}>
          <button
            type="button"
            onClick={() => setShowFilters(!showFilters)}
            style={{
              display: 'flex', alignItems: 'center', gap: '6px',
              background: 'none', border: 'none', cursor: 'pointer',
              fontSize: '0.8rem', fontWeight: 600, color: '#374151',
              padding: '4px 8px', borderRadius: '6px',
            }}
          >
            <Search size={15} style={{ color: '#9ca3af' }} />
            Filtros
            {showFilters
              ? <ChevronUp size={14} style={{ color: '#9ca3af' }} />
              : <ChevronDown size={14} style={{ color: '#9ca3af' }} />}
          </button>
          <div style={{ display: 'flex', gap: '8px' }}>
            {toolbarButtons}
          </div>
        </div>

        {/* Filtros colapsables */}
        {showFilters && (
          <div style={{
            padding: '16px',
            opacity: isDataLoading ? 0.5 : 1,
            pointerEvents: isDataLoading ? 'none' : 'auto',
            transition: 'opacity 0.2s',
          }}>
            <div style={{ maxWidth: '300px' }}>
              <label htmlFor="anoacademico" className="form-label">Año académico actual</label>
              <Input
                type="text" name="text" id="anoacademico"
                placeholder="Año académico actual" value={anoAcademico} disabled
              />
            </div>
          </div>
        )}
      </div>

      {dataLoaded && (
        <div style={{ marginTop: '16px' }}>
          <Table
            columns={[
              { Header: 'ID', accessor: 'id' },
              { Header: 'Nombre de alumno', accessor: 'alumnoName', width: 420 },
              { Header: 'ID Plan', accessor: 'idplan' },
              { Header: 'Nº matrícula', accessor: 'numMatricula' },
              { Header: 'Nota ciclo', accessor: 'notaCiclo' },
              { Header: 'Estado', accessor: 'estado' },
            ]}
            data={dataApi}
            defaultPageSize={10}
            showPaginationBottom
            className="-striped -highlight text-center"
          />
        </div>
      )}

      {isRunning && (
        <div style={{ marginTop: '16px' }}>
          <Row md="12">
            <Col md="12">
              <label htmlFor="progress" className="form-label">Progreso total</label>
              <Progress
                animated
                color={progressPercentage === 100 ? 'success' : 'primary'}
                value={progressPercentage}
                style={{ height: '25px' }}
              />
            </Col>
          </Row>
          <Row md="12" className="mt-2">
            <Col md="9">
              {progressPercentage === 100
                ? 'Cálculo completado'
                : `${recordsProcessed} de ${totalRecords} registros procesados`}
            </Col>
            <Col md="3">
              <p>ETA: {estimatedTimeRemaining}</p>
            </Col>
          </Row>
        </div>
      )}
    </>
  );
};

export default FormularioMotorCalculo;
